<!-- eslint-disable max-len -->
<template>
  <div
    class="portal-view"
  >
    <div
      v-if="!hasFetched"
      class="portal-loader"
    >
      <LoaderWithText />
    </div>

    <template v-else-if="clientPortalStore.isLoggedIn">
      <div
        v-if="!previewMode && clientPortalStore.portalRequest?.status === 'completed'"
        class="portal-complete"
      >
        <DocumentPortalComplete />
      </div>

      <template
        v-else-if="previewMode || ['active', 'opened'].includes(clientPortalStore.portalRequest?.status)"
      >
        <Stack
          class="portal-side"
        >
          <Stack>
            <Title size="4_5">
              Client Portal
            </Title>

            <Menu>
              <MenuItem
                v-for="section in clientPortalStore.menuSections"
                :key="section.id"
                :active="section.id === selectedSectionId"
                :icon-before="section.icon"
                show-icon-before
                :count="section.count"
                @click="selectedSectionId = section.id"
              >
                {{ section.label }}
              </MenuItem>
            </Menu>

            <Button
              variant="primary"
              :loading="clientPortalStore.isSubmitting"
              @click="handleSubmit"
            >
              Submit
            </Button>
          </Stack>

          <Stack>
            <AdvisorCard
              v-if="clientPortalStore.advisorInfo"
              :advisor-info="clientPortalStore.advisorInfo"
              :firm-info="clientPortalStore.firmInfo"
            />

            <ClientPortalUserStatus />
          </Stack>
        </Stack>

        <div class="portal-main">
          <div v-if="selectedSection.type === 'welcome'">
            <DocumentPortalWelcome
              @start="handleNext"
              @finish="handleSubmit"
            />
          </div>

          <Stack v-else-if="selectedSection.type === 'documents'">
            <div>
              <Title>
                {{ selectedSection.label }}
              </Title>
            </div>

            <PortalDocuments
              :entity-external-id="entityExternalId"
              :firm-slug="firmSlug"
              :advisor-slug="advisorSlug"
              portal-type="client"
            />
          </Stack>

          <Stack v-else-if="selectedSection.type === 'questionnaires'">
            <div>
              <Title>
                {{ selectedSection.label }}
              </Title>
            </div>

            <PortalQuestionnaires
              :entity-external-id="entityExternalId"
              :firm-slug="firmSlug"
              :advisor-slug="advisorSlug"
              portal-type="client"
            />
          </Stack>

          <div v-else-if="selectedSection.type === 'section'">
            <DossierItem
              v-if="selectedSectionId in dossierStore.resolvedProperties"
              :id="selectedSectionId"
              :ids="[]"
              :ids-with-array-indices="[]"
              :schema="dossierStore.resolvedProperties[selectedSectionId]"
              :depth="0"
              :array-index="0"
              :allowed-path-strings="clientPortalStore.allowedPathStrings"
              variant="client"
              @field-changed="() => {}"
            />
          </div>
        </div>
      </template>

      <div
        v-else
        class="portal-complete"
      >
        <PortalNotAvailable />
      </div>
    </template>

    <div
      v-else
      class="portal-login"
    >
      <PortalLogin />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';
import { useDossierStore } from '@frontend/ui/stores/dossierStore';
import { isEmptyValue, prettifyMixedCase } from '@frontend/shared/Util.js';
import { getValueAtPath } from '@frontend/shared/SchemaUtil.js';

import { Button, LoaderWithText, Menu, MenuItem, Stack, Title } from '@stakeui';
import AdvisorCard from '@frontend/ui/features/example/components/AdvisorCard.vue';
import ClientPortalUserStatus from '@frontend/ui/features/example/components/ClientPortalUserStatus.vue';
import DossierItem from '@frontend/ui/features/example/components/DossierItem.vue';
import DocumentPortalComplete from '@frontend/ui/features/example/components/DocumentPortalComplete.vue';
import DocumentPortalWelcome from '@frontend/ui/features/client-portal/components/DocumentPortalWelcome.vue';
import PortalDocuments from '../components/PortalDocuments.vue';
import PortalQuestionnaires from '../components/PortalQuestionnaires.vue';
import PortalNotAvailable from '../components/PortalNotAvailable.vue';
import PortalLogin from '../components/PortalLogin.vue';

// Define props
const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
  firmSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorSlug: {
    type: String,
    required: false,
    default: '',
  },
  portalType: {
    type: String,
    required: false,
    default: 'client', // 'firm', 'advisor', or 'client'
  },
  previewMode: {
    type: Boolean,
    default: false,
  },
  advisorView: {
    type: Boolean,
    default: false,
  },
});

const clientPortalStore = useClientPortalStore();
const dossierStore = useDossierStore();

const selectedSectionId = ref('welcome');
const hasFetched = ref(false);

function ensurePath(root, pathString, defaultLeafValue = null) {
  const parts = pathString.split('.');

  let cur = root;

  for (let i = 0; i < parts.length; i++) {
    const key = parts[i];
    const isLast = i === parts.length - 1;

    const next = parts[i + 1];
    const nextIsIndex = next !== undefined && /^\d+$/.test(next);
    const keyIsIndex = /^\d+$/.test(key);

    // If current container is an array, key should be an index
    if (Array.isArray(cur)) {
      const idx = keyIsIndex ? Number(key) : null;
      if (idx === null) return; // invalid path for current structure

      // ensure array length
      while (cur.length <= idx) cur.push(undefined);

      if (isLast) {
        if (cur[idx] === undefined) cur[idx] = defaultLeafValue;
        return;
      }

      // if missing, create next container based on what's coming
      if (cur[idx] === undefined || cur[idx] === null) {
        cur[idx] = nextIsIndex ? [] : {};
      }

      cur = cur[idx];
      continue;
    }

    // Otherwise current container is an object
    if (isLast) {
      if (cur[key] === undefined) cur[key] = defaultLeafValue;
      return;
    }

    if (cur[key] === undefined || cur[key] === null) {
      cur[key] = nextIsIndex ? [] : {};
    }

    cur = cur[key];
  }

  return cur;
}

const selectedSection = computed(() => clientPortalStore.menuSections.find(s => s.id === selectedSectionId.value));

// Handlers

const handleNext = () => {
  const idx = clientPortalStore.menuSections.findIndex(s => s.id === selectedSectionId.value);
  if (idx === -1) return null;
  selectedSectionId.value = clientPortalStore.menuSections[idx + 1]?.id ?? null;
};

// API

const handleSubmit = async () => {
  if (props.previewMode) {
    return;
  }

  let message = '';

  const missingFields = new Set();

  clientPortalStore.requiredFields.forEach(fieldPath => {
    const value = getValueAtPath(dossierStore.currentValue, fieldPath);

    if (isEmptyValue(value)) {
      missingFields.add(prettifyMixedCase(fieldPath[0]));
    }
  });

  if (missingFields.size > 0) {
    message += `These are missing required fields in:\n`;
    [...missingFields].forEach(path => message += `- ${path}\n`);
  }

  if (!clientPortalStore.documentsIsComplete) {
    message += `Some documents are missing.`;
  }

  if (message === '') {
    await clientPortalStore.submitPortalRequest(dossierStore.currentValue);
    return;
  }

  alert(message);
};

async function hydrateFromPortalStore() {
  const t = clientPortalStore.dossierTemplate;
  const d = clientPortalStore.dossier;

  if (!t) throw new Error('Missing dossierTemplate in clientPortalStore');
  if (!d) throw new Error('Missing dossier in clientPortalStore');

  // 1) schema
  dossierStore.setSchema(t);

  // 2) data (Vue 2 safe clone)
  const cloned = JSON.parse(JSON.stringify(d));

  for (const p of clientPortalStore.allowedPathStrings) {
    ensurePath(cloned, p, null);
  }

  dossierStore.setInitialValue(cloned);
}

onMounted(async () => {
  try {
    await clientPortalStore.initializePortal({
      token: props.token,
      entityExternalId: props.entityExternalId,
      advisorSlug: props.advisorSlug,
      firmSlug: props.firmSlug,
      advisorView: props.advisorView,
    });
    await hydrateFromPortalStore();
  } catch (error) {
    console.error(error);
  } finally {
    hasFetched.value = true;
  }
});
</script>

<style scoped>
.portal-view {
  background-color: hsl(var(--stk-text-h) var(--stk-text-s) 98%);
  display: flex;
  min-height: 100vh;
  align-items: stretch;
}

.portal-loader {
  align-self: start;
  padding: 2em;
}

.portal-side {
  border-right: 1px solid var(--stk-border);
  padding: 1.5em;
  flex-shrink: 0;
  width: 20em;
}

.portal-main {
  background-color: #fff;
  flex-grow: 1;
  padding: 2.5em 3em;
}

.portal-login,
.portal-complete {
  flex-grow: 1;
  display: flex;
  min-height: 100vh;
  align-items: center;
  padding: 3em;
}
</style>
